const LINE_EMOJI_ID_LENGTH = 3;
const LINE_EMOJI_TOKEN_REGEX = /\[line_emoji:([0-9A-Za-z]+):([0-9A-Za-z]+)\]/g;
const LINE_EMOJI_BASE_URL =
  'https://stickershop.line-scdn.net/sticonshop/v1/sticon';

const buildEmojiRange = count =>
  Array.from({ length: count }, (_, index) =>
    String(index + 1).padStart(LINE_EMOJI_ID_LENGTH, '0')
  );

export const LINE_EMOJI_PACKS = Object.freeze([
  {
    productId: '5ac1bfd5040ab15980c9b435',
    name: 'LINE Emoji 1',
    emojiIds: buildEmojiRange(250),
  },
  {
    productId: '5ac21a8c040ab15980c9b43f',
    name: 'LINE Emoji 2',
    emojiIds: buildEmojiRange(149),
  },
]);

export const normalizeLineEmojiId = rawId => {
  const value = String(rawId ?? '').trim();
  if (!value) {
    return '';
  }

  return value.padStart(LINE_EMOJI_ID_LENGTH, '0');
};

export const buildLineEmojiImageUrl = ({
  productId,
  emojiId,
  platform = 'android',
}) => {
  const safeProductId = String(productId || '').trim();
  const safeEmojiId = normalizeLineEmojiId(emojiId);
  if (!safeProductId || !safeEmojiId) {
    return '';
  }

  return `${LINE_EMOJI_BASE_URL}/${encodeURIComponent(
    safeProductId
  )}/${platform}/${encodeURIComponent(safeEmojiId)}.png`;
};

const normalizeLineEmojiItem = rawItem => {
  const item = rawItem?.toJSON ? rawItem.toJSON() : rawItem;
  if (!item || typeof item !== 'object') {
    return null;
  }

  const index = Number(item.index);
  if (!Number.isInteger(index) || index < 0) {
    return null;
  }

  const productId = String(item.productId || item.product_id || '').trim();
  const emojiId = normalizeLineEmojiId(item.emojiId || item.emoji_id);
  if (!productId || !emojiId) {
    return null;
  }

  const rawLength = Number(item.length);
  const length =
    Number.isInteger(rawLength) && rawLength > 0 ? rawLength : undefined;

  return {
    index,
    length,
    productId,
    emojiId,
  };
};

export const getLineEmojisFromContentAttributes = contentAttributes => {
  const attrs = contentAttributes || {};
  const rawEmojis = attrs.lineEmojis || attrs.line_emojis || attrs.emojis || [];
  if (!Array.isArray(rawEmojis)) {
    return [];
  }

  return rawEmojis
    .map(normalizeLineEmojiItem)
    .filter(Boolean)
    .sort((a, b) => a.index - b.index);
};

const buildLineEmojiMarkdown = (productId, emojiId, height) => {
  const imageUrl = buildLineEmojiImageUrl({ productId, emojiId });
  if (!imageUrl) {
    return '';
  }

  return `![line-emoji-${emojiId}](${imageUrl}?cw_image_height=${encodeURIComponent(
    height
  )})`;
};

export const replaceLineEmojiPlaceholdersWithMarkdown = (
  text,
  rawLineEmojis,
  { height = '22px' } = {}
) => {
  const message = String(text || '');
  const lineEmojis = getLineEmojisFromContentAttributes({
    lineEmojis: rawLineEmojis,
  });
  if (!lineEmojis.length) {
    return message;
  }

  let output = message;
  const sorted = [...lineEmojis].sort((a, b) => b.index - a.index);
  sorted.forEach(({ index, length, productId, emojiId }) => {
    if (index >= output.length) {
      return;
    }

    let replacementLength = 0;
    if (
      Number.isInteger(length) &&
      length > 0 &&
      index + length <= output.length
    ) {
      replacementLength = length;
    } else if (output[index] === '$') {
      replacementLength = 1;
    }

    if (replacementLength <= 0) return;

    const markdown = buildLineEmojiMarkdown(productId, emojiId, height);
    if (!markdown) {
      return;
    }

    output = `${output.slice(0, index)}${markdown}${output.slice(
      index + replacementLength
    )}`;
  });

  return output;
};

export const replaceInlineLineEmojiTokensWithMarkdown = (
  text,
  { height = '22px' } = {}
) => {
  return String(text || '').replace(
    LINE_EMOJI_TOKEN_REGEX,
    (_match, productId, emojiId) =>
      buildLineEmojiMarkdown(productId, normalizeLineEmojiId(emojiId), height)
  );
};

export const formatLineEmojiMessageForRendering = ({
  text,
  contentAttributes,
  height = '22px',
}) => {
  const lineEmojis = getLineEmojisFromContentAttributes(contentAttributes);
  const withPlaceholderReplaced = replaceLineEmojiPlaceholdersWithMarkdown(
    text,
    lineEmojis,
    { height }
  );

  return replaceInlineLineEmojiTokensWithMarkdown(withPlaceholderReplaced, {
    height,
  });
};
